import { cva, type VariantProps } from 'class-variance-authority'
import { Inbox, TriangleAlert } from 'lucide-react'
import type { HTMLAttributes, ReactNode } from 'react'
import { Cog } from '~/components/brand'
import { useT } from '~/lib/i18n'
import { cn } from '~/lib/utils'
import { Button } from './button'

// --- surfaces ---------------------------------------------------------------

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('relative rounded-card border border-border bg-surface shadow-card', className)}
      {...props}
    />
  )
}

/**
 * A row that behaves like a card but reacts to the pointer. Used for list items so a
 * long list still feels alive without every row shouting.
 */
export function CardRow({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-card border border-border bg-surface shadow-card',
        'transition-[border-color,box-shadow,transform] duration-200',
        'hover:-translate-y-px hover:border-border-strong hover:shadow-lift',
        // Amber rail that grows in on hover — the same cue as the active nav item.
        'before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-primary',
        'before:origin-top before:scale-y-0 before:transition-transform before:duration-200',
        'hover:before:scale-y-100',
        className,
      )}
      {...props}
    />
  )
}

/**
 * `CardRow` stood on its end, for grid views. The rail runs along the top instead of
 * the left edge, because in a grid the top is the shared edge the eye tracks across.
 */
export function CardTile({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-card border border-border bg-surface shadow-card',
        'transition-[border-color,box-shadow,transform] duration-200',
        'hover:-translate-y-0.5 hover:border-border-strong hover:shadow-lift',
        'before:absolute before:inset-x-0 before:top-0 before:z-10 before:h-[3px] before:bg-primary',
        'before:origin-left before:scale-x-0 before:transition-transform before:duration-200',
        'hover:before:scale-x-100',
        className,
      )}
      {...props}
    />
  )
}

/**
 * A tile's own controls, laid over its picture.
 *
 * On a tile the row of labelled buttons a list row can afford costs a whole line and
 * still ends up cramped. Over the picture they cost nothing, and the picture is the
 * one part of the card with room to spare. Every one carries an `aria-label` and a
 * `title` because an icon on its own is not a label.
 */
export function TileActions({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('absolute top-2 right-2 z-20 flex gap-1.5', className)} {...props} />
}

export function TileAction({
  icon,
  label,
  loading,
  onClick,
  className,
}: {
  icon: ReactNode
  label: string
  loading?: boolean
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={loading}
      onClick={(event) => {
        // Tiles are often wrapped in a <Link>. Managing the row is not navigating to it.
        event.preventDefault()
        event.stopPropagation()
        onClick()
      }}
      className={cn(
        'frosted grid size-9 place-items-center rounded-full border border-border text-ink-muted',
        'shadow-card transition-[color,border-color,background-color,transform] duration-150',
        'hover:border-ink-subtle hover:text-ink active:translate-y-px',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        'disabled:pointer-events-none disabled:opacity-60',
        className,
      )}
    >
      {loading ? <Cog className="size-4" /> : icon}
    </button>
  )
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-5',
        className,
      )}
      {...props}
    />
  )
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn('stencil text-ink-muted', className)} {...props} />
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-4 py-4 sm:px-5', className)} {...props} />
}

// --- badge ------------------------------------------------------------------

const badge = cva(
  'inline-flex items-center gap-1 rounded-chip px-2 py-[0.1875rem] text-[0.6875rem] ' +
    'font-bold uppercase tracking-[0.06em] whitespace-nowrap ring-1 ring-inset',
  {
    variants: {
      tone: {
        neutral: 'bg-surface-muted text-ink-muted ring-border-strong/60',
        primary: 'bg-primary-soft text-primary ring-primary-line',
        accent: 'bg-accent-soft text-accent ring-accent/25',
        success: 'bg-success-soft text-success ring-success/25',
        warning: 'bg-warning-soft text-warning ring-warning/30',
        danger: 'bg-danger-soft text-danger ring-danger/25',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
)

export function Badge({
  className,
  tone,
  ...props
}: HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badge>) {
  return <span className={cn(badge({ tone }), className)} {...props} />
}

/** A status dot for "live" facts — suspended, online, low stock. */
export function Dot({ tone = 'neutral' }: { tone?: 'success' | 'warning' | 'danger' | 'neutral' }) {
  const colour = {
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-danger',
    neutral: 'bg-ink-subtle',
  }[tone]
  return (
    <span className="relative flex size-2 shrink-0" aria-hidden>
      <span className={cn('absolute inset-0 rounded-full opacity-40 animate-pulse-soft', colour)} />
      <span className={cn('relative m-auto size-1.5 rounded-full', colour)} />
    </span>
  )
}

// --- states -----------------------------------------------------------------

export function Spinner({ className }: { className?: string }) {
  return <Cog className={cn('size-5 text-primary', className)} />
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn('relative overflow-hidden rounded-chip bg-surface-muted', className)}
      aria-hidden
    >
      <div className="shimmer absolute inset-0 animate-sweep opacity-70" />
    </div>
  )
}

/** Matches the height and rhythm of a real list row, so nothing jumps on load. */
export function ListSkeleton({
  rows = 4,
  height = 'h-[76px]',
}: {
  rows?: number
  height?: string
}) {
  return (
    <div className="space-y-2.5">
      {Array.from({ length: rows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: placeholders have no identity
        <Skeleton key={i} className={cn('w-full rounded-card', height)} />
      ))}
    </div>
  )
}

/**
 * Empty states teach the next action instead of saying "No data". For a
 * non-technical audience this is often the only onboarding they get.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    // No `.blueprint`: it renders inside an opaque card, and its grid would be out
    // of phase with the page's. One grid per screen, on the canvas.
    <div className="flex flex-col items-center gap-4 rounded-card px-6 py-14 text-center">
      <span className="hex grid size-14 place-items-center bg-surface-muted text-ink-subtle">
        {icon ?? <Inbox className="size-6" aria-hidden />}
      </span>
      <div className="space-y-1.5">
        <p className="text-base font-semibold text-ink">{title}</p>
        {description ? (
          <p className="mx-auto max-w-sm text-sm text-ink-muted">{description}</p>
        ) : null}
      </div>
      {action}
    </div>
  )
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const t = useT()
  return (
    <EmptyState
      icon={<TriangleAlert className="size-6 text-danger" aria-hidden />}
      title={t('ui.primitives.that_didnt_load')}
      description={message}
      action={
        onRetry ? (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            {t('ui.primitives.try_again')}
          </Button>
        ) : undefined
      }
    />
  )
}

/** Inline form-level failure, with a red edge so it registers before the text is read. */
export function FormError({ children }: { children: ReactNode }) {
  return (
    <p
      className="relative flex items-start gap-2 overflow-hidden rounded-control border border-danger/25 bg-danger-soft py-2.5 pr-3 pl-4 text-sm text-danger before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-danger"
      role="alert"
    >
      <TriangleAlert className="mt-px size-4 shrink-0" aria-hidden />
      {children}
    </p>
  )
}
